import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, ExternalLink, Eye, EyeOff, GripVertical, Image as ImageIcon, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ['Guides', 'Protocols', 'Supplies', 'General'];

export default function AdminResourcesTab({ adminFetch }) {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [formData, setFormData] = useState({
    title: '', description: '', url: '', category: 'Guides', sort_order: 0
  });
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchResources(); }, []);

  const fetchResources = async () => {
    try {
      const res = await adminFetch(`${API_URL}/api/admin/resources`);
      if (res.ok) setResources(await res.json());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setEditingResource(null);
    setFormData({ title: '', description: '', url: '', category: 'Guides', sort_order: resources.length });
    setThumbnailFile(null);
    setThumbnailPreview('');
    setShowModal(true);
  };

  const openEditModal = (resource) => {
    setEditingResource(resource);
    setFormData({
      title: resource.title,
      description: resource.description || '',
      url: resource.url,
      category: resource.category || 'General',
      sort_order: resource.sort_order || 0
    });
    setThumbnailFile(null);
    setThumbnailPreview(resource.thumbnail_url ? `${API_URL}${resource.thumbnail_url}` : '');
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.url.trim()) {
      toast.error('Title and URL are required');
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('title', formData.title);
      fd.append('description', formData.description);
      fd.append('url', formData.url);
      fd.append('category', formData.category);
      fd.append('sort_order', formData.sort_order);
      if (thumbnailFile) fd.append('thumbnail', thumbnailFile);

      const url = editingResource
        ? `${API_URL}/api/admin/resources/${editingResource.resource_id}`
        : `${API_URL}/api/admin/resources`;
      const method = editingResource ? 'PUT' : 'POST';

      const token = localStorage.getItem('adminToken');
      const res = await fetch(url, {
        method,
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd
      });

      if (res.ok) {
        toast.success(editingResource ? 'Resource updated' : 'Resource created');
        setShowModal(false);
        fetchResources();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to save');
      }
    } catch (err) {
      toast.error('Error saving resource');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (resource) => {
    if (!window.confirm(`Delete "${resource.title}"?`)) return;
    try {
      const res = await adminFetch(`${API_URL}/api/admin/resources/${resource.resource_id}`, { method: 'DELETE' });
      if (res.ok) {
        toast.success('Resource deleted');
        fetchResources();
      }
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const toggleVisibility = async (resource) => {
    try {
      const fd = new FormData();
      fd.append('visible', (!resource.visible).toString());
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/api/admin/resources/${resource.resource_id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd
      });
      if (res.ok) {
        toast.success(resource.visible ? 'Resource hidden' : 'Resource visible');
        fetchResources();
      }
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const handleThumbnailChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setThumbnailFile(file);
      setThumbnailPreview(URL.createObjectURL(file));
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-700" /></div>;
  }

  return (
    <div data-testid="admin-resources-tab">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-bold text-slate-900">Resources</h2>
          <p className="text-sm text-slate-500">Add links, guides, and helpful resources for your customers.</p>
        </div>
        <Button onClick={openAddModal} size="sm" className="bg-teal-700 hover:bg-teal-800" data-testid="add-resource-btn">
          <Plus className="h-4 w-4 mr-1" /> Add Resource
        </Button>
      </div>

      {resources.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-slate-400">
            <p className="text-base font-medium">No resources yet</p>
            <p className="text-sm mt-1">Click "Add Resource" to get started.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {resources.map((res, idx) => (
            <Card key={res.resource_id} className="overflow-hidden" data-testid={`admin-resource-${res.resource_id}`}>
              <div className="flex items-center gap-3 p-3">
                {/* Thumbnail */}
                <div className="w-20 h-14 rounded-lg bg-slate-100 overflow-hidden flex-shrink-0">
                  {res.thumbnail_url ? (
                    <img src={`${API_URL}${res.thumbnail_url}`} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><ImageIcon className="w-5 h-5 text-slate-300" /></div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm text-slate-900 truncate">{res.title}</span>
                    <Badge variant="outline" className="text-[10px] shrink-0">{res.category}</Badge>
                    {!res.visible && <Badge variant="secondary" className="text-[10px] shrink-0 bg-amber-100 text-amber-700">Hidden</Badge>}
                  </div>
                  {res.description && <p className="text-xs text-slate-500 truncate mt-0.5">{res.description}</p>}
                  <a href={res.url} target="_blank" rel="noopener noreferrer" className="text-xs text-teal-600 hover:underline inline-flex items-center gap-1 mt-0.5">
                    <ExternalLink className="w-3 h-3" /> {res.url.length > 50 ? res.url.substring(0, 50) + '...' : res.url}
                  </a>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 shrink-0">
                  <Button variant="ghost" size="sm" onClick={() => toggleVisibility(res)} title={res.visible ? 'Hide' : 'Show'} data-testid={`toggle-resource-${res.resource_id}`}>
                    {res.visible ? <Eye className="h-4 w-4 text-green-600" /> : <EyeOff className="h-4 w-4 text-slate-400" />}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => openEditModal(res)} data-testid={`edit-resource-${res.resource_id}`}>
                    <Pencil className="h-4 w-4 text-blue-600" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(res)} data-testid={`delete-resource-${res.resource_id}`}>
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="resource-modal">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-semibold text-slate-900">{editingResource ? 'Edit Resource' : 'Add Resource'}</h3>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSave} className="p-4 space-y-4">
              <div>
                <Label>Title *</Label>
                <Input
                  value={formData.title}
                  onChange={e => setFormData(p => ({ ...p, title: e.target.value }))}
                  placeholder="How to Reconstitute Peptides"
                  data-testid="resource-title-input"
                />
              </div>
              <div>
                <Label>URL *</Label>
                <Input
                  value={formData.url}
                  onChange={e => setFormData(p => ({ ...p, url: e.target.value }))}
                  placeholder="https://youtube.com/watch?v=..."
                  data-testid="resource-url-input"
                />
              </div>
              <div>
                <Label>Description (optional)</Label>
                <Input
                  value={formData.description}
                  onChange={e => setFormData(p => ({ ...p, description: e.target.value }))}
                  placeholder="Short description of this resource"
                  data-testid="resource-description-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Category</Label>
                  <select
                    value={formData.category}
                    onChange={e => setFormData(p => ({ ...p, category: e.target.value }))}
                    className="w-full h-9 rounded-md border border-slate-200 bg-white px-3 text-sm"
                    data-testid="resource-category-select"
                  >
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Sort Order</Label>
                  <Input
                    type="number"
                    value={formData.sort_order}
                    onChange={e => setFormData(p => ({ ...p, sort_order: parseInt(e.target.value) || 0 }))}
                    data-testid="resource-sort-input"
                  />
                </div>
              </div>
              <div>
                <Label>Thumbnail</Label>
                <div className="mt-1">
                  {thumbnailPreview ? (
                    <div className="relative w-full aspect-video rounded-lg overflow-hidden border bg-slate-50">
                      <img src={thumbnailPreview} alt="" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => { setThumbnailFile(null); setThumbnailPreview(''); }}
                        className="absolute top-2 right-2 bg-black/60 rounded-full p-1 text-white hover:bg-black/80"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center w-full aspect-video border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:border-slate-300 bg-slate-50">
                      <ImageIcon className="w-8 h-8 text-slate-300 mb-1" />
                      <span className="text-xs text-slate-400">Click to upload thumbnail</span>
                      <input type="file" accept="image/*" className="hidden" onChange={handleThumbnailChange} data-testid="resource-thumbnail-input" />
                    </label>
                  )}
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
                <Button type="submit" disabled={saving} className="bg-teal-700 hover:bg-teal-800" data-testid="save-resource-btn">
                  {saving ? 'Saving...' : editingResource ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
